/**
 * AI tools in-flight operation store.
 *
 * Module-level external store for the video node "AI" toolbar menu
 * (Slice / Extract depth). The in-flight flag must outlive the node
 * component: ReactFlow's `onlyRenderVisibleElements` unmounts nodes that
 * leave the viewport and remounts them on return, which reset the previous
 * component-local `useState` and re-enabled the button mid-request (a second
 * click then created a duplicate clip group).
 *
 * Keyed by (projectId, canvasId, sourceNodeId). Read via
 * `useSyncExternalStore` in `useDepthVideoAction`.
 */
import { createNadDebug } from '../../../../utils/debugLog'

export type AiToolsOperation = 'ai-clip' | 'depth'

type Listener = () => void

// Namespace supplies feature=depth-video, concern=client on every record.
const log = createNadDebug('dw:depth-video:client')

const operations = new Map<string, AiToolsOperation>()
const listeners = new Map<string, Set<Listener>>()

export function aiToolsOperationKey(projectId: string, canvasId: string, nodeId: string): string {
  return JSON.stringify([projectId, canvasId, nodeId])
}

export function getAiToolsOperation(key: string): AiToolsOperation | null {
  return operations.get(key) ?? null
}

export function subscribeAiToolsOperation(key: string, listener: Listener): () => void {
  let keyListeners = listeners.get(key)
  if (!keyListeners) {
    keyListeners = new Set()
    listeners.set(key, keyListeners)
  }
  keyListeners.add(listener)
  return () => {
    const current = listeners.get(key)
    if (!current) return
    current.delete(listener)
    if (current.size === 0) listeners.delete(key)
  }
}

function setOperation(key: string, operation: AiToolsOperation | null): void {
  if (operation === null) operations.delete(key)
  else operations.set(key, operation)
  log({
    event: 'ai-tools:operation_changed',
    key,
    active_operation: operation,
  })
  listeners.get(key)?.forEach((listener) => listener())
}

/**
 * Single-flight runner. Starts `run` only when no operation is in flight for
 * `key`; returns `null` (and does not call `run`) otherwise. The operation is
 * cleared when the returned promise settles, regardless of whether the node
 * that started it is still mounted.
 */
export function startAiToolsOperation<T>(
  key: string,
  operation: AiToolsOperation,
  run: () => Promise<T>,
): Promise<T> | null {
  const active = operations.get(key)
  if (active !== undefined) {
    log({
      event: 'ai-tools:start_rejected',
      key,
      requested_operation: operation,
      active_operation: active,
    })
    return null
  }
  setOperation(key, operation)
  let pending: Promise<T>
  try {
    pending = run()
  } catch (error) {
    setOperation(key, null)
    throw error
  }
  return pending.finally(() => setOperation(key, null))
}

/** Test-only: drop all state between unit tests. */
export function resetAiToolsOperationsForTests(): void {
  operations.clear()
  listeners.clear()
}
